/**
 * Sesiunea de builder pe situl clientului: preia tokenul de handoff din
 * fragmentul URL-ului (#aig=..., pus de dashboardul nostru), îl ține în
 * localStorage după schimb și îl expiră local. Fragmentul se șterge din URL
 * imediat - nu ajunge nici în istoricul vizibil, nici pe serverul clientului.
 */

export interface StoredBuilderSession {
  token: string;
  expiresAt: number;
}

function storageKey(appId: string): string {
  return `aig_builder_${appId}`;
}

export interface BuilderFragment {
  /** tokenul de handoff (#aig=...), decodat - vezi createHandoffToken. */
  handoff: string | null;
  /** starea de scan cărată peste un salt cross-origine (#aigscan=...), raw base64 - vezi scan.ts encodeScanCarry. */
  scanCarry: string | null;
}

/**
 * Extrage `aig=`/`aigscan=` dintr-un fragment de URL, fără atinge `location` -
 * pură și testabilă. Ambele chei se scot ÎNTR-O SINGURĂ trecere, altfel a doua
 * extragere ar opera pe un fragment deja rescris de prima și le-ar putea
 * strica reciproc (ex. index greșit după `replace`). O rută SPA rămasă în
 * fragment (`#/checkout`) supraviețuiește neatinsă.
 */
export function parseBuilderFragment(hash: string): BuilderFragment & { cleanedHash: string } {
  const params = new Map<string, string>();
  const rest: string[] = [];
  // `#/route&aig=A&aigscan=B` → segmentul întâi (`/route`) nu e `key=value`,
  // deci rămâne în `rest` neatins; restul se parsează ca pereche cheie=valoare.
  for (const segment of hash.replace(/^#/, "").split("&")) {
    if (!segment) continue;
    const eq = segment.indexOf("=");
    const key = eq === -1 ? null : segment.slice(0, eq);
    if (key === "aig" || key === "aigscan") {
      params.set(key, segment.slice(eq + 1));
    } else {
      rest.push(segment);
    }
  }
  const cleanedHash = rest.length > 0 ? `#${rest.join("&")}` : "";
  return {
    handoff: params.has("aig") ? decodeURIComponent(params.get("aig")!) : null,
    scanCarry: params.has("aigscan") ? decodeURIComponent(params.get("aigscan")!) : null,
    cleanedHash,
  };
}

/**
 * Preia handoff-ul de builder (#aig=..., pus de dashboardul nostru la
 * deschiderea inițială, sau de un salt cross-origine - vezi
 * builder/index.ts → navigateToNext) ȘI starea de scan cărată alături
 * (#aigscan=...), într-o singură citire a fragmentului URL-ului - vezi
 * `parseBuilderFragment` pentru motivul unei singure treceri. Fragmentul se
 * curăță imediat: nu ajunge nici în istoricul vizibil, nici pe serverul
 * clientului.
 */
export function takeBuilderFragmentFromUrl(): BuilderFragment {
  const { handoff, scanCarry, cleanedHash } = parseBuilderFragment(location.hash);
  if (handoff !== null || scanCarry !== null) {
    history.replaceState(null, "", location.pathname + location.search + cleanedHash);
  }
  return { handoff, scanCarry };
}

export function loadBuilderSession(appId: string): StoredBuilderSession | null {
  try {
    const raw = localStorage.getItem(storageKey(appId));
    if (!raw) return null;
    const session = JSON.parse(raw) as StoredBuilderSession;
    if (!session.token || session.expiresAt <= Date.now()) {
      localStorage.removeItem(storageKey(appId));
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function saveBuilderSession(appId: string, session: StoredBuilderSession): void {
  try {
    localStorage.setItem(storageKey(appId), JSON.stringify(session));
  } catch {
    // storage indisponibil (private mode etc.) - sesiunea rămâne doar în memorie
  }
}

/** Șterge sesiunea locală de builder - la „Close" din panou, ca refresh-ul să nu-l mai redeschidă. */
export function clearBuilderSession(appId: string): void {
  try {
    localStorage.removeItem(storageKey(appId));
  } catch {
    // storage indisponibil - nimic de curățat
  }
}
